import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { groupBy, getNotificationObject, hasWriteAccess, parseEnumStart } from "../../../utils/utils";
import { isEditActiveSelector, editedObjectIdSelector } from "../../../reducers/editReducer";
import { EditActions, editActions } from "../../../actions/editActions";
import StandardBoardsList from "./StandardBoardsList/StandarBoardsList";
import AddButton from "../../../components/AddButton/AddButton";
import { createEmptyStandardBoard, createEmptyStandardPoint } from "../../../utils/pdApiUtils";
import { Dimmer, Loader } from "semantic-ui-react";
import EditStandardBoard from "../../Editing/EditStandardBoard";
import IoPointsList from "./IoPointsList/IoPointsList";
import { pdApiActions, PdApiActions } from "../../../actions/pdApiActions";
import { ioConfigActions, IoConfigActions } from "../../../actions/ioConfigActions";
import EditStandardNode from "../../Editing/EditStandardNode";
import EditStandardAction from "../../Editing/EditStandardAction";
import EditStandardPoint from "../../Editing/EditStandardPoint";
import { warning } from "react-notification-system-redux";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { NavLink } from "react-router-dom";
import SearchEvents from "../SearchEvents/SearchEvents";
import arrowLeft from "../../../images/arrow-left-button.svg";
import rightPoint from "../../../images/ico-chevron-right.svg";

type StateProps = {
  pdApi: State.PdApi;
  mainState: State.Main;
  hasEdited: boolean | undefined;
  editedObjectId: string;
  isEditActive: boolean;
  ioConfig: State.IoConfig;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
  ioConfigActions: typeof ioConfigActions;
  showWarning: typeof warning;
};

type OwnProps = {};
type ViewEditors =
  | "standardBoards"
  | "standardNodes"
  | "standardActions"
  | "standardPoints"
  | undefined;

export type StandardBoardsProps = StateProps & DispatchProps & OwnProps;

const StandardBoards = (props: StandardBoardsProps) => {
  // Local UI states
  const [isModalOpen, SetModalOpen] = React.useState<boolean>(false);
  const [editor, setEditor] = React.useState<ViewEditors>(undefined);
  const [isFecthingPoints, SetIsFecthingPoints] = React.useState<boolean>(false);
  const [results, setResults] = React.useState<Api.AnyItem[] | null>(null);
  const [searchText, setSearchText] = React.useState("");
  const [currentResultIndex, setCurrentResultIndex] = React.useState(0);
  const [scrollToPointId, setScrollToPointId] = React.useState<string | undefined>(undefined);
  const { pdApi, mainState, isEditActive, ioConfig, hasEdited } = props;
  const { activeUser } = mainState;
  const [currentBoard, setCurrentBoard] = React.useState<string | undefined>(undefined);
  const [currentRevision, setCurrentRevision] = React.useState<string | undefined>(undefined);
  const [currentNodeCPU, setCurrentNodeCPU] = React.useState<string | undefined>(undefined);
  const [currentNodeNetwork, setCurrentNodeNetwork] = React.useState<string | undefined>(undefined);
  const [currentNodePoint, setCurrentNodePoint] = React.useState<string | undefined>(undefined);

  const standardBoards: Api.StandardBoard[] = ioConfig.standardBoards;

  const groupedBoards = groupBy(standardBoards, "boardType", "enumName");

  const standardPoints =
    ioConfig.standardPoints.filter(
      point => point.parentNodeId === ioConfig.selectedStandardBoard.standardNodeId
    ) ?? null;

  const getStandardAction = (actionId: string, pointId: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const standardAction = ioConfig.standardPoints
      ?.find(point => point.id === pointId)
      ?.standardActions.find(action => action.id === actionId);

    return standardAction ?? null;
  };

  const isActionInput = (parentPointId: string): boolean =>
    !!ioConfig.standardPoints.find(point => point.id === parentPointId)?.isInput;

  const clearActionSelection = () => {
    props.ioConfigActions.setSelectedStandardActionId("");
    props.editActions.cancelEditing();
    setEditor(undefined);
  };

  const handleBackClick = () => { 
    setCurrentBoard(undefined);
    setCurrentRevision(undefined);
    setCurrentNodeCPU(undefined);
    setCurrentNodeNetwork(undefined);
    setCurrentNodePoint(undefined);
    props.ioConfigActions.setSelectedStandardBoardId("");
    props.ioConfigActions.setSelectedStandardNodeId("");
    props.ioConfigActions.setSelectedStandardPointId("");
    clearActionSelection();
  };

  // onClick handles
  const handleActionClick = (actionId: string, pointId: string) => {
    if (hasEdited) {
      props.showWarning(
        getNotificationObject("warning", "Cannot select new entry when another has been edited.", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedStandardActionId(actionId);
    props.ioConfigActions.setSelectedStandardPointId(pointId);
    setEditor("standardActions");
    const selectedStandardAction = getStandardAction(actionId, pointId);
    if (selectedStandardAction) {
      props.editActions.select(selectedStandardAction);
      const nodePoint = ioConfig.standardPoints.find(item => item.id === pointId);
      const node = nodePoint && ioConfig.standardBoards
        .flatMap(board => board.standardNodes)
        .find(n => n.id === nodePoint.parentNodeId);
      const board = node && ioConfig.standardBoards.find(item => item.id === node.parentBoardId);

      if (!!board && !!node && !!nodePoint) {
        const trimmedCPU = node.cpu.enumName?.toString().startsWith("IO_CPU_") ? node.cpu.enumName.replace("IO_CPU_", "") : node.cpu.enumName;
        const trimmedNetwork = node.network.enumName?.toString();
        setCurrentBoard(board?.boardType.enumName);
        setCurrentRevision(board?.revision);
        setCurrentNodeCPU(trimmedCPU + " [" + node.cpu.enumValue + "]");
        setCurrentNodeNetwork(trimmedNetwork + " [" + node.network.enumValue + "]");
        setCurrentNodePoint(nodePoint.isInput ? "Input_Index " + nodePoint.ioindex.toString() : "Output_Index " + nodePoint.ioindex.toString());
      }
    }
  };

  const getTargetAction = (
    standardActions: Api.StandardAction[],
    newSearchText: string
  ): Api.StandardAction[] =>
    standardActions.filter(
      action =>
        action.eventClassEnumNavigation.enumName.includes(newSearchText.toUpperCase()) ||
        action.eventTypeEnumNavigation.enumName.includes(newSearchText.toUpperCase()) ||
        newSearchText.toUpperCase().includes(action.eventClassEnumNavigation.enumName + " [" + action.eventClassEnumNavigation.enumValue + "]") ||
        newSearchText.toUpperCase().includes(action.eventTypeEnumNavigation.enumName+ " [" + action.eventTypeEnumNavigation.enumValue + "]")
    );

  const handleOnSearchSubmitClick = (newSearchText: string, nodeId: string | undefined): void => {
    if (nodeId) {
      const points = ioConfig.standardPoints.filter(
        (point: Api.StandardPoint) => point.parentNodeId === nodeId
      );

      const pointsWithActions = points.filter(point => !!point.standardActions);
      const resultList = pointsWithActions.reduce((_resultList: Internal.IOSearchItem[], point) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const targetActions = getTargetAction(point.standardActions!, newSearchText);
        if (targetActions.length) {
          const results: Internal.IOSearchItem[] = targetActions.map(result => {
            const item: Internal.IOSearchItem = {
              id: result.id,
              parentPointId: result.parentPointId,
              index: point.ioindex,
              isInput: point.isInput ? 1 : 0
            };
            return item;
          });
          return [..._resultList, ...results];
        }
        return _resultList;
      }, []);

      /// So we can jump to next result in same order than they are displayed
      const resultsSortedByIndex = resultList.sort((a, b) => {
        if (a.isInput === b.isInput) {
          return a.index - b.index;
        } else {
          return b.isInput - a.isInput;
        }
      });
      setResults(resultsSortedByIndex);
      if (resultsSortedByIndex.length > 0) {
        setScrollToPointId(resultsSortedByIndex[0].parentPointId);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        handleActionClick(resultsSortedByIndex[0].id!, resultsSortedByIndex[0].parentPointId!);
      }
    } else {
      handleSearchNextNodeClick(searchText, true);
    }
  };
  const handleSearchNextNodeClick = (searchTerm: string, getFirstNode?: boolean) => {
    let nextNodesParentRevisionId = "";
    let nextNodeId = "";
    let nextBoardTypeName = "";
    let getNextNodeIdFlag = getFirstNode;
    standardBoards.forEach((board: Api.StandardBoard) => {
      board.standardNodes?.forEach(node => {
        if (getNextNodeIdFlag) {
          nextNodesParentRevisionId = board.id;
          nextNodeId = node.id ?? "";
          nextBoardTypeName = board.boardType.enumName;
          getNextNodeIdFlag = false;
          return;
        }
        if (node.id === ioConfig.selectedIoBoard.ioNodeId) {
          getNextNodeIdFlag = true;
        }
      });
    });

    props.pdApiActions.fetchSingle("standardBoards", nextNodesParentRevisionId);
    props.ioConfigActions.setSelectedStandardNodeId(nextNodeId);
    if (
      nextNodesParentRevisionId !== "" &&
      !ioConfig.openedRows.standardBoards.includes(nextNodesParentRevisionId)
    ) {
      props.ioConfigActions.updateOpenedStandardBoards(nextNodesParentRevisionId);
    }
    if (
      nextBoardTypeName !== "" &&
      !ioConfig.openedRows.standardBoardTypes.includes(nextBoardTypeName)
    ) {
      props.ioConfigActions.updateOpenedBoardTypes(nextBoardTypeName);
    }

    handleOnSearchSubmitClick(searchTerm, nextNodeId);
  };

  const handleNextResultClick = (direction: "up" | "down") => {
    if (results && results.length > 0) {
      /// if nextIndex goes over the lenght of results go back to zero, or end of list in reverse
      const nextIndex =
        direction === "down"
          ? currentResultIndex + 1 >= results.length
            ? 0
            : currentResultIndex + 1
          : currentResultIndex - 1 < 0
            ? results.length - 1
            : currentResultIndex - 1;
      setCurrentResultIndex(nextIndex);
      setScrollToPointId(results[nextIndex].parentPointId);
      handleActionClick(results[nextIndex].id, results[nextIndex].parentPointId);
    }
  };

  const handleSetSearchText = (newSearchText: string) => {
    setSearchText(newSearchText);
  };
  const clearSearchResults = () => {
    /// clear search results when search input is changed to hide Jump to next arrow buttons and not have unsynchronized ui elements
    if (results && results.length > 0) {
      setResults(null);
      setCurrentResultIndex(0);
    }
  };

  const handleAddBoardClick = () => {
    if (!hasEdited) {
      handleBackClick();
      setEditor("standardBoards");
      props.editActions.startEditing(createEmptyStandardBoard(pdApi), true);
    } else {
          props.showWarning(
            getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
          );
          return null;
        }
  };
  const handleBoardEditClick = (boardId: string) => {//idex,side edit    
    if (!hasEdited) {
      props.ioConfigActions.setSelectedStandardNodeId("");
      props.ioConfigActions.setSelectedStandardPointId("");
      clearActionSelection();
      setEditor("standardBoards");
      const selectedStandardBoard = standardBoards.find(board => board.id === boardId);
      if (selectedStandardBoard) {
        props.editActions.select(selectedStandardBoard);
        if (selectedStandardBoard.id !== ioConfig.selectedStandardBoard.standardBoardId) {
          props.ioConfigActions.setSelectedStandardBoardId(boardId);
        }
      }
      setCurrentBoard(selectedStandardBoard?.boardType?.enumName);
      setCurrentRevision(selectedStandardBoard?.revision);
      setCurrentNodeCPU(undefined);
      setCurrentNodeNetwork(undefined);
      setCurrentNodePoint(undefined);
    } else {
        props.showWarning(
          getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
        );
        return null;
      }
  };
  const handleRevisionClick = (id: string) => {
    if (hasEdited) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedStandardNodeId("");
    props.ioConfigActions.setSelectedStandardPointId("");
    clearActionSelection();
     if (id) {
       const selectedBoard = standardBoards.filter(item => item.id === id);
       if (selectedBoard) {
         setCurrentBoard(selectedBoard[0]?.boardType?.enumName);
         if(ioConfig.openedRows.standardBoards.includes(selectedBoard[0]?.id)){
            setCurrentRevision(undefined);
          }else {
            setCurrentRevision(selectedBoard[0]?.revision);
          }
         setCurrentNodeCPU(undefined);
         setCurrentNodeNetwork(undefined);
         setCurrentNodePoint(undefined);
       }
     }
    props.ioConfigActions.updateOpenedStandardBoards(id);
    const nodes = ioConfig.standardBoards.find(board => board.id === id)?.standardNodes ?? [];
    if (nodes[0]?.standardPoints === null) {
      props.pdApiActions.fetchSingle("standardBoards", id);
    }
    props.ioConfigActions.setSelectedStandardBoardId(id);
  };
  const handleBoardTypeClick = (id: string) => {//onclick of board
    if (hasEdited) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedStandardNodeId("");
    props.ioConfigActions.setSelectedStandardPointId("");
    clearActionSelection();
    props.ioConfigActions.updateOpenedBoardTypes(id);
    if (id) {
      if (ioConfig.openedRows.standardBoardTypes.includes(id)){//to suppport open close of board types
        setCurrentBoard(undefined);
      } else {
        setCurrentBoard(id);
      }
      setCurrentRevision(undefined);
      setCurrentNodeCPU(undefined);
      setCurrentNodeNetwork(undefined);
      setCurrentNodePoint(undefined);
    }
  };

  const handleNodeClick = (node: Api.StandardNode) => {//on node click    
    if (hasEdited) {
      props.showWarning(
        getNotificationObject("warning", "Cannot select new entry when another has been edited.", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedStandardPointId("");
    clearActionSelection();
     props.ioConfigActions.setSelectedStandardActionId("");
     if (node.parentBoardId) {
       const selectedBoard = standardBoards.filter(item => item.id === node.parentBoardId);
       if (selectedBoard) {
      const trimmedCPU = node.cpu.enumName?.toString().startsWith("IO_CPU_") ? node.cpu.enumName.replace("IO_CPU_", "") : node.cpu.enumName;
      const trimmedNetwork = node.network.enumName?.toString();
      setCurrentBoard(selectedBoard[0]?.boardType?.enumName);
      setCurrentRevision(selectedBoard[0]?.revision);
      setCurrentNodeCPU(trimmedCPU + " [" + node.cpu.enumValue + "]");
      setCurrentNodeNetwork(trimmedNetwork + " [" + node.network.enumValue + "]");
      setCurrentNodePoint(undefined);
      }
     }
    if (
      props.ioConfig.selectedStandardBoard.standardBoardId === node.parentBoardId &&
      props.ioConfig.localFetchingStatus === "Fetching"
    ) {
      SetIsFecthingPoints(true);
    }
    if (node.parentBoardId !== ioConfig.selectedStandardBoard.standardBoardId) {
      props.ioConfigActions.setSelectedStandardBoardId(node.parentBoardId);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    props.ioConfigActions.setSelectedStandardNodeId(node.id!);
  };
  const handleNodeEditClick = (node: Api.AnyItem) => {//I guess not invoked
    if (!hasEdited) {
      if (
        props.ioConfig.selectedStandardBoard.standardBoardId === node.parentBoardId &&
        props.ioConfig.localFetchingStatus === "Fetching"
      ) {
        SetIsFecthingPoints(true);
      }
      props.editActions.cancelEditing();
      setEditor("standardNodes");
      props.ioConfigActions.setSelectedStandardNodeId(node.id);
    }
    props.editActions.select(node);
  };

  const handleAddPointClick = () => {
    if (!hasEdited) {
      clearActionSelection();
      props.ioConfigActions.setSelectedStandardPointId("");
      setCurrentNodePoint(undefined);
      const parentNodeId = ioConfig.selectedStandardBoard.standardNodeId;
      if (parentNodeId) {
        setEditor("standardPoints");
        props.editActions.startEditing(createEmptyStandardPoint(parentNodeId), true);
      }
    } else {
          props.showWarning(
            getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
          );
          return null;
        }
  };
  const handlePointClick = (id: string) => {//I guess not used
    clearActionSelection();
    props.ioConfigActions.setSelectedStandardPointId(id);
  };
  const handlePointEditClick = (point: Api.StandardPoint) => {
    if (hasEdited) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    clearActionSelection();
    props.ioConfigActions.setSelectedStandardPointId(point.id);
    setCurrentNodePoint(point?.isInput ? "Input_Index " + point?.ioindex.toString() : "Output_Index " + point?.ioindex.toString());
    if (!hasEdited) {
      props.ioConfigActions.setSelectedStandardActionId("");
      props.editActions.cancelEditing();
      setEditor("standardPoints");
    }
    props.editActions.select(point);
  };

  // eslint-disable-next-line
  const handleReturnToIoConfig = (e:any) => {
    if(props.hasEdited){
      props.showWarning(
        getNotificationObject("warning", "The standard action editor is still open or there are some unsaved changes. Please close or save or cancel them first!", { autoDismiss: 10 })
      );
      e.preventDefault();
      return;
    }
    clearSearchResults();
    setSearchText("");

    const emptyStandardOpenedRows = {
      standardBoardTypes: [],
      standardBoards: [],
      ioBoardTypes: props.ioConfig.openedRows.ioBoardTypes,
      ioBoards: props.ioConfig.openedRows.ioBoards,
      ioRevisions: props.ioConfig.openedRows.ioRevisions
    };
    props.ioConfigActions.setOpenedRows(emptyStandardOpenedRows);

    handleBackClick();
  };
  

    /// end of click handlers
  const toggleModal = (): void => {
    SetModalOpen(!isModalOpen);
  };
  // When mounting get necessary data to show boards/revisions/nodes
  React.useEffect(() => {
    if (standardBoards.length < 1) {
      props.pdApiActions.fetchStandardBoardsShallow();
    };
  }, []);
  React.useEffect(() => {
    if (!props.ioConfig.localFetchingStatus) {
      SetIsFecthingPoints(false);
    }
  }, [props.ioConfig.localFetchingStatus]);
  React.useEffect(() => {
    if (searchText && props.ioConfig.selectedStandardBoard.standardNodeId) {
      handleOnSearchSubmitClick(searchText, props.ioConfig.selectedStandardBoard.standardNodeId);
    }
  }, [props.ioConfig.selectedStandardBoard.standardNodeId]);

  React.useEffect(() => {
        // eslint-disable-line
  }, [currentBoard, currentRevision, currentNodeCPU, currentNodeNetwork, currentNodePoint]);

  React.useEffect(() => {
      // Update current state based on selected IDs - runs only on component mount
      if (!props.ioConfig.standardBoards) return;
      const standardBoards = props.ioConfig.standardBoards;
      // Handle selectedStandardBoardId
      if (props.ioConfig.selectedStandardBoard?.standardBoardId && standardBoards.length > 0) {
        const selectedBoard = standardBoards.filter(board => board.id === props.ioConfig.selectedStandardBoard?.standardBoardId);
        if (selectedBoard) {
          setCurrentBoard(selectedBoard[0]?.boardType?.enumName);
          setCurrentRevision(selectedBoard[0]?.revision);
          setCurrentNodeCPU(undefined);
          setCurrentNodeNetwork(undefined);
          setCurrentNodePoint(undefined);
        }
      }
      // Handle selectedStandardNodeId
      if (props.ioConfig.selectedStandardBoard?.standardNodeId 
        && props.ioConfig.selectedStandardBoard?.standardBoardId 
        && standardBoards.length > 0) {
        const selectedNode = standardBoards
          .flatMap(board => board.standardNodes)
          .find(node => node.id === props.ioConfig.selectedStandardBoard?.standardNodeId);        
        if (selectedNode?.parentBoardId) {
          const selectedBoard = standardBoards.filter(item => item.id === selectedNode.parentBoardId);
          if (selectedBoard) {
            const trimmedCPU = selectedNode.cpu.enumName?.toString().startsWith("IO_CPU_") ? selectedNode.cpu.enumName.replace("IO_CPU_", "") : selectedNode.cpu.enumName;
            const trimmedNetwork = selectedNode.network.enumName?.toString();
            setCurrentBoard(selectedBoard[0]?.boardType?.enumName);
            setCurrentRevision(selectedBoard[0]?.revision);
            setCurrentNodeCPU(trimmedCPU + " [" + selectedNode.cpu.enumValue + "]");
            setCurrentNodeNetwork(trimmedNetwork + " [" + selectedNode.network.enumValue + "]");
            setCurrentNodePoint(undefined);
          }
        }
      }
      // Handle selectedStandardPointId
      if (props.ioConfig.selectedStandardBoard?.standardPointId 
        && props.ioConfig.selectedStandardBoard?.standardNodeId 
        && props.ioConfig.selectedStandardBoard?.standardBoardId 
        && props.ioConfig.standardPoints
        && standardBoards.length > 0) {
          const selectedPoint = props.ioConfig.standardPoints.find(point => point.id === props.ioConfig.selectedStandardBoard?.standardPointId);
          const selectedNode = selectedPoint && props.ioConfig?.standardBoards
              .flatMap(board => board.standardNodes)
              .find(n => n.id === selectedPoint?.parentNodeId);
          const selectedBoard = selectedNode && props.ioConfig?.standardBoards.find(item => item.id === selectedNode?.parentBoardId);
          if (!!selectedBoard && !!selectedNode && !!selectedPoint) {
            const trimmedCPU = selectedNode.cpu.enumName?.toString().startsWith("IO_CPU_") ? selectedNode.cpu.enumName.replace("IO_CPU_", "") : selectedNode.cpu.enumName;
            const trimmedNetwork = selectedNode.network.enumName?.toString();
            setCurrentBoard(selectedBoard?.boardType.enumName);
            setCurrentRevision(selectedBoard?.revision);
            setCurrentNodeCPU(trimmedCPU + " [" + selectedNode.cpu.enumValue + "]");
            setCurrentNodeNetwork(trimmedNetwork + " [" + selectedNode.network.enumValue + "]");
            setCurrentNodePoint(selectedPoint.isInput ? "Input_Index " + selectedPoint.ioindex.toString() : "Output_Index " + selectedPoint.ioindex.toString());
          }
        }
    }, [props.ioConfig.standardBoards, props.ioConfig.standardPoints]); // eslint-disable-line

  const getEditor = (editorType: ViewEditors) => {
    switch (editorType) {
      case "standardBoards":
        return <EditStandardBoard toggleConfirm={toggleModal} {...props} />;
      case "standardNodes":
        return <EditStandardNode toggleConfirm={toggleModal} {...props} />;
      case "standardPoints":
        return <EditStandardPoint toggleConfirm={toggleModal} {...props} />;
      default:
        return null;
    }
  };
  const ioBoardsHeight = window.innerHeight - 220;
  const Editor = getEditor(editor);

  // eslint-disable-next-line
  const  getBoardNameAndRev = (editedObjectId:string): string => 
  {
    const boardName = props.ioConfig.standardBoards.find(sb=>sb.id===editedObjectId)?.boardType.enumName;
    const revision = props.ioConfig.standardBoards.find(sb=>sb.id===editedObjectId)?.revision;
    return("\"" + boardName + "\" revision \"" + revision + "\"");
  };
  
  const buildModal = () => {
    const content = (
      <span>
        Are you sure you want to delete this {editor?.slice(0, -1)}?
        <br />
        <br />
        {"\"" + currentBoard + "\" revision \"" + currentRevision + "\""}
        {/*getBoardNameAndRev(props.editedObjectId)*/}
        <br />
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() => props.editActions.delete(editor as Api.OptionsGrud)}
        onCancel={toggleModal}
      />
    );
  };
  return (
    <div className="io-wrapper">
      {(ioConfig.localFetchingStatus === "Updating" ||
        ioConfig.localFetchingStatus === "Adding") && (
        <Dimmer active={true} inverted={true}>
          <Loader content={ioConfig.localFetchingStatus} />
        </Dimmer>
      )}
      <div className="io-header">
        <div className="io-header-title">
          <NavLink to="/tools/io-configuration">
            <img src={arrowLeft} alt="Go back" onClick={(e) => {
              handleReturnToIoConfig(e);
              }
            }/>
          </NavLink>
          <h2>Standard Boards </h2>
          {currentBoard && (
            <>
              <img src={rightPoint}  id="pathIoStdConfiguration" alt="right point" />
              <span>{parseEnumStart(currentBoard, 2)}</span>
            </>
          )}
          {currentRevision && (
            <>
              <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
              <span id="pathTitleIoStd">Revision&nbsp;{currentRevision}</span>
              <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
              <span id="pathTitleIoStd">Index&nbsp;{0}</span>
              <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
              <span id="pathTitleIoStd">Side&nbsp;{0}</span>
            </>
          )}
          {currentNodeCPU && (
            <>
              <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
              <span id="pathTitleIoStd">{currentNodeCPU}</span>
            </>
          )}
          {currentNodeNetwork && (
            <>
              <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
              <span id="pathTitleIoStd">{currentNodeNetwork}</span>
            </>
          )}
          {(currentNodePoint) && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">
                  {currentNodePoint?.split(" ")[0]}{" "}
                  {currentNodePoint?.split(" ")[1]}
                </span>
              </>
            )}
        </div>
        <div className="io-search">
          <SearchEvents
            // onSearchNextNodeClick={handleSearchNextNodeClick}
            searchText={searchText}
            setSearchText={handleSetSearchText}
            currentNodeId={ioConfig.selectedStandardBoard.standardNodeId}
            currentIndex={currentResultIndex}
            onSearchTextChange={clearSearchResults}
            numberOfResults={results ? results.length : null}
            onNextResultClick={handleNextResultClick}
            onSearchSubmitClick={handleOnSearchSubmitClick}
          />
        </div>
      </div>
      <div className="io-content-wrapper">
        <div className="io-boards" >
          <div className="io-boards-header">
            <span>Boards</span>
            {hasWriteAccess(activeUser, "Lift") ? (
              <AddButton
                onClick={() => handleAddBoardClick()}
                title="Add board"
                id="addBoardButton"
              />
            ) : (
              <AddButton
                onClick={() => handleAddBoardClick()}
                title="Add board"
                id="disablebutton"
              />
            )}
          </div>
          <div style={{ height: ioBoardsHeight + "px", overflowY: "scroll" }}>
            <StandardBoardsList
              rows={groupedBoards}
              parent="name"
              child="revision"
              onNodeSelect={handleNodeClick}
              onRevisionClick={handleRevisionClick}
              onNodeEditClick={handleNodeEditClick}
              onBoardTypeClick={handleBoardTypeClick}
              onBoardEditClick={handleBoardEditClick}
            />
          </div>
        </div>

        {ioConfig.selectedStandardBoard.standardNodeId && (
          <div className="io-boards" >
            <IoPointsList
              scrollToPointId={scrollToPointId}
              loading={isFecthingPoints}
              standardPoints={standardPoints}
              onActionClick={handleActionClick}
              selectedActionId={ioConfig.selectedStandardBoard.standardActionId}
              onAddNewPointClick={handleAddPointClick}
              onPointClick={handlePointClick}
              onPointEditClick={handlePointEditClick}
              activeUser={activeUser}
            />
          </div>
        )}
        {isEditActive &&
          editor === "standardActions" &&
          ioConfig.selectedStandardBoard.standardPointId &&
          ioConfig.selectedStandardBoard.standardActionId && (
            <div className="io-action-editor">
              <EditStandardAction
                {...props}
                toggleModal={toggleModal}
                isInput={isActionInput(ioConfig.selectedStandardBoard.standardPointId)}
                actionType="standardActions"
              />
            </div>
          )}
      </div>
      {isEditActive &&
        (editor === "standardPoints" || editor === "standardBoards" || editor === "standardNodes") && 
        (hasEdited ? (
          <Dimmer active={!!hasEdited} page={true}>
            <div className="edit-view-hasEdited">{Editor}</div>
          </Dimmer>
        ) : (
          <Dimmer active={true} page={true}>
            <div className="edit-view">{Editor}</div>
          </Dimmer>
        ))}
      {buildModal()}
    </div>
  );
};
const mapStateToProps = (state: State.Root) => ({
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  hasEdited: state.editView.hasEdited,
  editedObjectId: editedObjectIdSelector(state.editView),
  ioConfig: state.ioConfig
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | IoConfigActions | PdApiActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  ioConfigActions: bindActionCreators({ ...ioConfigActions }, dispatch),
  showWarning: bindActionCreators(warning, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardBoards);
